<template>
  <section
    ref="filtersRoot"
    class="dir-top-bar"
    :class="{ 'dir-top-bar--popover-open': anyPopoverOpen }"
  >
    <div class="dir-top-bar__row">
      <div class="dir-top-bar__chips" role="group">
        <div
          v-if="props.propertyCheckboxes.length"
          class="dir-chip-wrap"
        >
          <button
            type="button"
            class="dir-chip"
            :class="{
              'dir-chip--active': !!propertyTypeSummary,
              'dir-chip--open': popovers.propertyType,
            }"
            :aria-expanded="popovers.propertyType"
            @click="onCellClick('propertyType')"
          >
            <span class="dir-chip__text text-body-2">
              {{ propertyTypeSummary || $t("directory.filters.type") }}
            </span>
            <base-icon
              name="base/ChevronDown"
              size="14px"
              :rot="popovers.propertyType ? '180deg' : '0deg'"
              color="currentColor"
            />
          </button>
          <div
            v-if="popovers.propertyType"
            class="dir-popover__panel dir-popover__panel--compact"
            @click.stop
          >
            <ul class="dir-popover__list">
              <li
                v-for="item in props.propertyCheckboxes"
                :key="item.server_label"
              >
                <label class="dir-popover__row">
                  <input
                    type="checkbox"
                    class="dir-popover__checkbox"
                    :checked="item.checked"
                    @change="props.handlePropertyTypeCheckboxClick(item)"
                  >
                  <span class="dir-popover__row-name">{{ item.label }}</span>
                  <span class="dir-popover__row-count">{{ item.count }}</span>
                </label>
              </li>
            </ul>
          </div>
        </div>

        <div class="dir-chip-wrap">
          <button
            type="button"
            class="dir-chip"
            :class="{
              'dir-chip--active': !!countrySummary,
              'dir-chip--open': popovers.country,
            }"
            :aria-expanded="popovers.country"
            @click="onCellClick('country')"
          >
            <span class="dir-chip__text text-body-2">
              {{ countrySummary || $t("directory.filters.country") }}
            </span>
            <base-icon
              name="base/ChevronDown"
              size="14px"
              :rot="popovers.country ? '180deg' : '0deg'"
              color="currentColor"
            />
          </button>
          <div
            v-if="popovers.country"
            class="dir-popover__panel"
            @click.stop
          >
            <div class="dir-popover__head">
              <span class="dir-popover__title">
                {{ $t("directory.filters.country") }}
              </span>
              <button
                v-if="hasSelectedCountries"
                type="button"
                class="dir-popover__clear"
                @click="props.handleCountryReset"
              >
                {{ $t("directory.filters.clear_all") }}
              </button>
            </div>
            <input
              ref="countrySearchInput"
              v-model="countryFilter"
              type="text"
              :placeholder="$t('search_filters.fr_by_coun')"
              class="dir-popover__search"
              @click.stop
            >
            <ul class="dir-popover__list">
              <li
                v-for="item in filteredCountryCheckboxes"
                :key="`country-${item.server_label}`"
              >
                <label class="dir-popover__row">
                  <input
                    type="checkbox"
                    class="dir-popover__checkbox"
                    :checked="item.checked"
                    @change="props.handleCountryCheckboxClick(item)"
                  >
                  <span class="dir-popover__row-name">
                    {{ countryLabel(item.label) }}
                  </span>
                  <span class="dir-popover__row-count">{{ item.count }}</span>
                </label>
              </li>
              <li
                v-if="!filteredCountryCheckboxes.length"
                class="dir-popover__empty"
              >
                {{ $t("main.m_no_results") }}
              </li>
            </ul>
          </div>
        </div>

        <div class="dir-chip-wrap">
          <button
            type="button"
            class="dir-chip"
            :class="{
              'dir-chip--active': !!priceSummary,
              'dir-chip--open': popovers.price,
            }"
            :aria-expanded="popovers.price"
            @click="onCellClick('price')"
          >
            <span class="dir-chip__text text-body-2">
              {{ priceSummary || $t("directory.filters.price_range") }}
            </span>
            <base-icon
              name="base/ChevronDown"
              size="14px"
              :rot="popovers.price ? '180deg' : '0deg'"
              color="currentColor"
            />
          </button>
          <div
            v-if="popovers.price"
            class="dir-popover__panel dir-popover__panel--compact"
            @click.stop
          >
            <base-price-range-slider
              :max-price="filtersStoreRef.priseRange?.priceMax"
              :min-price="filtersStoreRef.priseRange?.priceMin"
              :range="filtersStoreRef.priseRange?.range"
              style-type="filters"
              no-delay
              :drop-func="dropPrice"
              @emit-range="
                (data) =>
                  props.updateSliderFilter('price_range', 'tokenPrice', data)
              "
            />
            <button
              v-if="priceSummary"
              type="button"
              class="dir-popover__clear dir-popover__clear--bottom"
              @click="onPriceReset"
            >
              {{ $t("directory.filters.reset") }}
            </button>
          </div>
        </div>

        <div
          v-if="props.propertyType !== 'lands'"
          class="dir-chip-wrap"
        >
          <button
            type="button"
            class="dir-chip"
            :class="{
              'dir-chip--active': !!aprSummary,
              'dir-chip--open': popovers.apr,
            }"
            :aria-expanded="popovers.apr"
            @click="onCellClick('apr')"
          >
            <span class="dir-chip__text text-body-2">
              {{
                aprSummary
                  ? `${$t("directory.filters.yield_apr")} ${aprSummary}`
                  : $t("directory.filters.yield_apr")
              }}
            </span>
            <base-icon
              name="base/ChevronDown"
              size="14px"
              :rot="popovers.apr ? '180deg' : '0deg'"
              color="currentColor"
            />
          </button>
          <div
            v-if="popovers.apr"
            class="dir-popover__panel dir-popover__panel--compact"
            @click.stop
          >
            <base-navigator-filters-input-range
              name="apr"
              after="%"
              :maxlength="3"
              is-slider
              :slider-max="filtersStoreRef.currentStats?.aprMax"
              :slider-min="filtersStoreRef.currentStats?.aprMin"
              :drop-func="dropApr"
              @change="(data) => props.updateSliderFilter('apr', 'apr', data)"
            />
            <button
              v-if="aprSummary"
              type="button"
              class="dir-popover__clear dir-popover__clear--bottom"
              @click="onAprReset"
            >
              {{ $t("directory.filters.reset") }}
            </button>
          </div>
        </div>

        <div
          v-if="props.propertyType !== 'lands'"
          class="dir-chip-wrap"
        >
          <button
            type="button"
            class="dir-chip"
            :class="{
              'dir-chip--active': !!irrSummary,
              'dir-chip--open': popovers.irr,
            }"
            :aria-expanded="popovers.irr"
            @click="onCellClick('irr')"
          >
            <span class="dir-chip__text text-body-2">
              {{
                irrSummary
                  ? `${$t("directory.filters.yield_irr")} ${irrSummary}`
                  : $t("directory.filters.yield_irr")
              }}
            </span>
            <base-icon
              name="base/ChevronDown"
              size="14px"
              :rot="popovers.irr ? '180deg' : '0deg'"
              color="currentColor"
            />
          </button>
          <div
            v-if="popovers.irr"
            class="dir-popover__panel dir-popover__panel--compact"
            @click.stop
          >
            <base-navigator-filters-input-range
              name="irr"
              after="%"
              :maxlength="3"
              is-slider
              :slider-max="filtersStoreRef.currentStats?.irrMax"
              :slider-min="filtersStoreRef.currentStats?.irrMin"
              :drop-func="dropIrr"
              @change="(data) => props.updateSliderFilter('irr', 'irr', data)"
            />
            <button
              v-if="irrSummary"
              type="button"
              class="dir-popover__clear dir-popover__clear--bottom"
              @click="onIrrReset"
            >
              {{ $t("directory.filters.reset") }}
            </button>
          </div>
        </div>

        <div class="dir-chip-wrap">
          <button
            type="button"
            class="dir-chip"
            :class="{
              'dir-chip--active': !!minInvestSummary,
              'dir-chip--open': popovers.minInvest,
            }"
            :aria-expanded="popovers.minInvest"
            @click="onCellClick('minInvest')"
          >
            <span class="dir-chip__text text-body-2">
              {{ minInvestSummary || $t("directory.filters.min_investment") }}
            </span>
            <base-icon
              name="base/ChevronDown"
              size="14px"
              :rot="popovers.minInvest ? '180deg' : '0deg'"
              color="currentColor"
            />
          </button>
          <div
            v-if="popovers.minInvest"
            class="dir-popover__panel dir-popover__panel--compact"
            @click.stop
          >
            <base-navigator-filters-input-range
              name="minimumInvestment"
              is-slider
              :slider-max="filtersStoreRef.currentStats?.maximumInvestment"
              :slider-min="filtersStoreRef.currentStats?.minimumInvestment"
              :drop-func="dropMinInvest"
              conver-able
              @change="
                (data) =>
                  props.updateSliderFilter(
                    'min_invest',
                    'minimumInvestment',
                    data
                  )
              "
            />
            <button
              v-if="minInvestSummary"
              type="button"
              class="dir-popover__clear dir-popover__clear--bottom"
              @click="onMinInvestReset"
            >
              {{ $t("directory.filters.reset") }}
            </button>
          </div>
        </div>
      </div>

      <div class="dir-top-bar__actions">
        <button
          type="button"
          class="dir-top-bar__action"
          :class="{ 'dir-top-bar__action--badged': props.allFiltersCount > 0 }"
          @click="$emit('open-all-filters')"
        >
          <base-icon name="base/Sliders" size="16px" />
          <span class="text-body-2 text-bold">
            {{ $t("directory.filters.all_filters") }}
          </span>
          <span
            v-if="props.allFiltersCount > 0"
            class="dir-top-bar__badge text-body-3 text-bold"
          >
            {{ props.allFiltersCount }}
          </span>
        </button>
        <slot name="actions" />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { filtersStore } from "~/store/search_filters";
import { countryNames } from "~/utilities/constants/countryCodes";
import eventBus from "~/utilities/composables/eventBus";
import type {
  FilterCheckbox,
  PropertyTypes,
} from "~/components/base/navigator/types";
import { BASE_CURRENCY } from "~/composables/CURRENCIES";

interface Props {
  search: string;
  propertyType: PropertyTypes;
  allFiltersCount: number;
  propertyCheckboxes: FilterCheckbox[];
  countryCheckboxes: FilterCheckbox[];
  handlePropertyTypeCheckboxClick: (item: FilterCheckbox) => void;
  handleCountryCheckboxClick: (item: FilterCheckbox) => void;
  handleCountryReset: () => void;
  updateSliderFilter: (
    key: string,
    paramName: string,
    values: Record<string, Record<string, number | string>>
  ) => void;
  resetSliderFilter: (
    key: string,
    paramName: string,
    resetValues: number[],
    isDrop?: boolean
  ) => void;
}

const props = defineProps<Props>();
defineEmits<{
  search: [val: string];
  "open-all-filters": [];
}>();

const filtersStoreRef = filtersStore();
const { t } = useI18n();
const route = useRoute();

const popovers = reactive({
  propertyType: false,
  country: false,
  price: false,
  apr: false,
  irr: false,
  minInvest: false,
});

type PopoverKey = keyof typeof popovers;

const anyPopoverOpen = computed(() =>
  (Object.keys(popovers) as PopoverKey[]).some((k) => popovers[k])
);

const filtersRoot = ref<HTMLElement | null>(null);
const countrySearchInput = ref<HTMLInputElement | null>(null);
const countryFilter = ref("");

const closeAllPopovers = () => {
  (Object.keys(popovers) as PopoverKey[]).forEach((k) => {
    popovers[k] = false;
  });
};

const onCellClick = async (which: PopoverKey) => {
  const wasOpen = popovers[which];
  closeAllPopovers();
  popovers[which] = !wasOpen;
  if (which === "country" && popovers[which]) {
    await nextTick();
    countrySearchInput.value?.focus();
  }
};

const onDocumentClick = (e: MouseEvent) => {
  const root = filtersRoot.value;
  if (!root) return;
  if (!root.contains(e.target as Node)) {
    closeAllPopovers();
  }
};

onMounted(() => document.addEventListener("click", onDocumentClick));
onBeforeUnmount(() => document.removeEventListener("click", onDocumentClick));

const filteredCountryCheckboxes = computed(() => {
  const q = countryFilter.value.trim().toLowerCase();
  if (!q) return props.countryCheckboxes;
  return props.countryCheckboxes.filter((item) =>
    countryLabel(item.label).toLowerCase().includes(q)
  );
});

const countryLabel = (label: string) => {
  if (!label || label === "null") return t("search_filters.fr_others");
  const localized =
    t(`countries.${label}`) !== `countries.${label}`
      ? t(`countries.${label}`)
      : countryNames[label];
  return localized || label;
};

const hasSelectedCountries = computed(() =>
  props.countryCheckboxes.some((item) => item.checked)
);

const formatMoneyShort = (val: number | string | null | undefined): string => {
  const n = typeof val === "string" ? parseFloat(val) : (val ?? 0);
  const sym = BASE_CURRENCY.value.symbol;
  if (!Number.isFinite(n) || n <= 0) return `${sym}0`;
  if (n >= 1000) {
    const k = Math.round(n / 100) / 10;
    return `${sym}${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k`;
  }
  return `${sym}${Math.round(n)}`;
};

const propertyTypeSummary = computed(() => {
  const active = props.propertyCheckboxes.filter((i) => i.checked);
  if (!active.length) return "";
  if (active.length === 1) return active[0].label;
  return `${t("directory.filters.type")} · ${active.length}`;
});

const countrySummary = computed(() => {
  const selected = props.countryCheckboxes.filter((i) => i.checked);
  if (!selected.length) return "";
  const first = countryLabel(selected[0].label);
  if (selected.length === 1) return first;
  return `${first} +${selected.length - 1}`;
});

const priceSummary = computed(() => {
  const gte = route.query["tokenPrice[gte]"];
  const lte = route.query["tokenPrice[lte]"];
  if (gte == null && lte == null) return "";
  return `${formatMoneyShort(gte as string)}–${formatMoneyShort(lte as string)}`;
});

const aprSummary = computed(() => {
  const gte = route.query["apr[gte]"];
  const lte = route.query["apr[lte]"];
  if (gte == null && lte == null) return "";
  return `${gte ?? 0}–${lte ?? "∞"}%`;
});

const irrSummary = computed(() => {
  const gte = route.query["irr[gte]"];
  const lte = route.query["irr[lte]"];
  if (gte == null && lte == null) return "";
  return `${gte ?? 0}–${lte ?? "∞"}%`;
});

const minInvestSummary = computed(() => {
  const gte = route.query["minimumInvestment[gte]"];
  const lte = route.query["minimumInvestment[lte]"];
  if (gte == null && lte == null) return "";
  return `${formatMoneyShort(gte as string)}–${formatMoneyShort(lte as string)}`;
});

const dropPrice = () => {
  return function (ctx: (nums: [number, number]) => void) {
    eventBus.on("drop-tokenPrice-range", ctx);
  };
};
const dropApr = () => {
  return function (ctx: (nums: [number, number]) => void) {
    eventBus.on("drop-apr-range", ctx);
  };
};
const dropIrr = () => {
  return function (ctx: (nums: [number, number]) => void) {
    eventBus.on("drop-irr-range", ctx);
  };
};
const dropMinInvest = () => {
  return function (ctx: (nums: [number, number]) => void) {
    eventBus.on("drop-minimumInvestment-range", ctx);
  };
};

const onPriceReset = () => {
  props.resetSliderFilter("price_range", "tokenPrice", [
    filtersStoreRef.currentStats?.token?.priceMin ?? 0,
    filtersStoreRef.currentStats?.token?.priceMax ?? 1,
  ]);
};

const onAprReset = () => {
  props.resetSliderFilter("apr", "apr", [
    filtersStoreRef.currentStats?.aprMin ?? 0,
    filtersStoreRef.currentStats?.aprMax ?? 100,
  ]);
};

const onIrrReset = () => {
  props.resetSliderFilter("irr", "irr", [
    filtersStoreRef.currentStats?.irrMin ?? 0,
    filtersStoreRef.currentStats?.irrMax ?? 100,
  ]);
};

const onMinInvestReset = () => {
  props.resetSliderFilter("min_invest", "minimumInvestment", [
    filtersStoreRef.currentStats?.minimumInvestment ?? 0,
    filtersStoreRef.currentStats?.maximumInvestment ?? 100,
  ]);
};
</script>

<style scoped lang="scss">
.dir-top-bar {
  position: relative;
  z-index: 10;
  background: var(--header-bg);
  width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  // Full-bleed background, but inset the chips horizontally to the page content
  // edge so they line up with the logo / listing column (not the raw viewport
  // edge). calc(50vw - 50%) is the gap from the viewport edge to the wrapper
  // content edge; max() keeps a 16px gutter on narrow / full-width layouts.
  padding: 16px max(16px, calc(50vw - 50%));
  box-sizing: border-box;

  &--popover-open {
    z-index: 20;
  }
}

.dir-top-bar__row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  flex-wrap: wrap;
}

.dir-top-bar__chips {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 1 auto;
  flex-wrap: wrap;
  min-width: 0;
}

.dir-chip-wrap {
  position: relative;
  flex-shrink: 0;
}

.dir-chip {
  max-width: 240px;
  @include drop-button-styles;
  @include ds-chip-idle;
  &--active {
    max-width: 240px;
    @include ds-chip-active;
  }
  /* Compound selector — open always wins over active when both are on */
  &.dir-chip--open {
    color: var(--text-primary);
    background-color: var(--surface);
    border: 1px solid var(--border-subtle);
    box-shadow: var(--shadow-card-hover);
  }
}

.dir-chip__text {
  display: inline-block;
  min-width: 0;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: inherit;
}

.dir-top-bar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.dir-top-bar__action {
  @include drop-button-styles;
  @include ds-chip-idle;
  border-color: transparent;
  box-shadow: var(--shadow-card);
  transition: box-shadow 0.2s ease;
  &:hover {
    border-color: transparent;
    box-shadow: var(--shadow-card-hover);
  }
  &--badged {
    border-color: var(--border-strong);
  }
}

.dir-top-bar__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: var(--radius-pill);
  background: var(--accent-primary);
  color: var(--surface);
}

@media (max-width: 56rem) {
  .dir-top-bar {
    padding: 10px 16px;
  }
  .dir-top-bar__row {
    flex-wrap: nowrap;
    gap: 8px;
  }
  /* Mobile keeps only the "All filters" CTA pulled to the left — the inline
     chip strip is hidden to declutter the narrow viewport. */
  .dir-top-bar__chips {
    display: none;
  }
  .dir-top-bar__actions {
    flex: 1 1 auto;
    margin-left: 0;
    justify-content: space-between;
  }
}

@media (max-width: 32rem) {
  .dir-top-bar {
    padding: 10px 12px;
  }
}
</style>

<style lang="scss">
.dir-popover__panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  background: var(--white-monochrome);
  border-radius: 16px;
  box-shadow:
    -4px 12px 28px rgba(18, 18, 18, 0.1),
    4px 12px 28px rgba(18, 18, 18, 0.1),
    0 8px 16px rgba(18, 18, 18, 0.04);
  padding: 16px 20px 18px;
  min-width: 300px;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-family: inherit;
  z-index: 100;
  text-align: left;
  transform-origin: top center;
  animation: dirPopoverIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both;

  &--compact {
    min-width: 260px;
    max-width: 300px;
  }
}

@keyframes dirPopoverIn {
  from {
    opacity: 0;
    transform: translateY(-6px) scale(0.985);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.dir-popover__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.dir-popover__title {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--black-monochrome-60);
}

.dir-popover__clear {
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  color: var(--black-monochrome-72);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  &:hover {
    color: var(--black-monochrome);
  }
}

.dir-popover__clear--bottom {
  align-self: flex-end;
  margin-top: 4px;
}

.dir-popover__search {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--gray-monochrome-40);
  border-radius: 10px;
  font-size: 14px;
  background: var(--white-monochrome);
  outline: none;
  color: var(--black-monochrome);
  &:focus {
    border-color: var(--black-monochrome);
  }
  &::placeholder {
    color: var(--black-monochrome-60);
  }
}

.dir-popover__list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 280px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.dir-popover__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  color: var(--black-monochrome);
  &:hover {
    background: var(--white-contrast);
  }
}

.dir-popover__checkbox {
  width: 18px;
  height: 18px;
  margin: 0;
  accent-color: var(--black-monochrome);
  cursor: pointer;
  flex-shrink: 0;
}

.dir-popover__row-name {
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dir-popover__row-count {
  font-size: 12px;
  color: var(--black-monochrome-60);
  flex-shrink: 0;
}

.dir-popover__empty {
  padding: 16px 10px;
  text-align: center;
  font-size: 13px;
  color: var(--black-monochrome-60);
}

.dir-popover__hint {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--black-monochrome-60);
}

.dir-popover__panel .input-range-wrapper {
  .input-range {
    .custom-input {
      background: transparent;
      border-color: var(--black-monochrome-60);

      input {
        color: var(--black-monochrome-60);
        background: transparent;
      }

      &::after {
        color: var(--black-monochrome-60);
      }
    }
  }
}

.dir-popover__panel .base-price-range.base-price-range--filters {
  width: 100%;
  min-width: 0;
  max-width: 100%;

  .chart-wrapper {
    width: 100%;
    max-width: 100%;

    canvas {
      max-width: 100% !important;
      width: 100% !important;
    }

    .vue-slider {
      max-width: 100% !important;
      width: 100% !important;
    }
  }

  .price-range {
    width: 100%;
    gap: 9px;

    .price-input {
      flex: 1 1 0;
      width: auto;
      max-width: none;
      min-width: 0;

      input {
        max-width: 100%;
        width: 100%;
      }
    }

    .base-separator {
      flex-shrink: 0;
    }
  }
}
</style>
